import { useState } from 'react'
import { invoiceUrl, setCartLine } from '../api/client'
import type { Order, OrderLine } from '../api/types'
import { useCart } from '../cart'
import { useLandName } from '../laender'
import { useDatumFormat, useSprache } from '../i18n'
import { usePriceFormat } from '../shopConfig'
import { Sendungskarte, Standmarke, useKopieren, Verfolgung } from './OrderTracker'

/**
 * Eine Bestellung, wie sie dasteht.
 *
 * Eigenes Bauteil, weil drei Stellen dieselbe Bestellung zeigen: die
 * Bestätigung nach dem Kauf, die Nachschau mit Nummer und E-Mail und die Liste
 * des angemeldeten Kunden. Drei Darstellungen derselben Sache wären zwei zu
 * viel — die selten angefasste veraltet.
 *
 * Zwei Spalten ab 900 Pixeln, darunter untereinander. Links steht, was mit der
 * Bestellung *passiert* — wo sie steht, was drin ist, wo das Paket ist, und was
 * man jetzt damit tun kann. Rechts steht, was sie *gekostet* hat und wohin sie
 * geht. Das ist die Trennung, nach der ein Kunde sucht: Wer die Seite öffnet,
 * will entweder wissen „wo ist mein Paket" oder „was habe ich bezahlt", und
 * fast nie beides zugleich.
 *
 * Die Positionen stehen deshalb zweimal da — links ohne Preis, rechts mit. Das
 * ist keine Verdopplung aus Versehen: Links beantworten sie „was kommt da",
 * rechts „wofür ist das Geld weg". Eine Liste, die beides tut, ist auf 390
 * Pixeln eine Zeile mit vier Angaben und beantwortet keine der beiden Fragen.
 */
export function Bestellansicht({
  bestellung,
  email,
}: {
  bestellung: Order
  /**
   * Die E-Mail, mit der die Bestellung geöffnet wurde.
   *
   * Sie ist zusammen mit der Nummer der Schlüssel — ohne sie gibt es keine
   * Rechnung zum Herunterladen, und der Knopf bleibt weg. Ein Verweis, der ein
   * PDF verspricht und eine Fehlerseite liefert, ist schlimmer als keiner.
   */
  email?: string
}) {
  const { t } = useSprache()
  const preis = usePriceFormat()
  const datum = useDatumFormat()
  const sendungen = bestellung.shipments ?? []
  const bestelltAm = datum(bestellung.createdAt, true)
  const { kopiert, kopieren } = useKopieren(bestellung.number)

  const rechnung = bestellung.hasInvoice && email ? invoiceUrl(bestellung.number, email) : null

  return (
    <section
      className="bestellansicht"
      aria-label={t('bestellung.aria', { nummer: bestellung.number })}
    >
      {/* Die Kopfleiste läuft über beide Spalten: Nummer, Zeitpunkt und Stand
          gehören zur ganzen Bestellung und nicht in eine der beiden Hälften. */}
      <header className="bestellansicht-kopf">
        <div className="bestellansicht-kennung">
          <span className="bestellansicht-nummer">{bestellung.number}</span>
          {/* Die Nummer wird abgetippt, vorgelesen oder in eine Mail geklebt —
              dafür ein Knopf statt der Bitte, sie sauber zu markieren. */}
          <button
            className="kopierknopf"
            type="button"
            onClick={kopieren}
            aria-label={t('bestellung.nummerKopieren')}
            title={t('bestellung.nummerKopieren')}
          >
            {kopiert ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m5 12.5 4.5 4.5L19 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect
                  x="9"
                  y="9"
                  width="11"
                  height="11"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="M15 5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
          {/* Blass gegenüber kräftig sagt einer Sprachausgabe nichts — deshalb
              die Rückmeldung auch in Worten. */}
          <span className="nur-fuer-screenreader" role="status">
            {kopiert ? t('bestellung.nummerKopiert') : ''}
          </span>
        </div>

        {bestelltAm && (
          <span className="bestellansicht-zeit">
            {t('bestellung.bestelltAm', { datum: bestelltAm })}
          </span>
        )}

        <Standmarke status={bestellung.status} />
      </header>

      <div className="bestellansicht-spalten">
        <div className="bestellansicht-spalte">
          <section className="bestellkasten">
            <h2 className="bestellkasten-titel">{t('bestellung.fortschritt')}</h2>
            <Verfolgung bestellung={bestellung} />
          </section>

          <section className="bestellkasten">
            <h2 className="bestellkasten-titel">{t('bestellung.artikel')}</h2>
            <ul className="bestellposten">
              {bestellung.lines.map((zeile, index) => (
                <Posten key={`a-${zeile.name}-${zeile.variant}-${index}`} zeile={zeile} />
              ))}
            </ul>
          </section>

          {/* Mehrere Sendungen sind der Normalfall, sobald etwas nachgeliefert
              wird — deshalb eine Liste und kein Kasten. */}
          {sendungen.map((sendung, index) => (
            <Sendungskarte
              key={`${sendung.trackingCode}-${index}`}
              sendung={sendung}
              nummer={index + 1}
              anzahl={sendungen.length}
            />
          ))}

          <div className="bestellansicht-aktionen">
            {rechnung && (
              /* Ein Verweis und kein Knopf: Das Herunterladen macht der
                 Browser. In einem neuen Fenster, damit die Bestellung stehen
                 bleibt — wer aus dem PDF-Betrachter zurückkommt, landete sonst
                 auf einem leeren Suchformular. */
              <a
                className="knopf"
                href={rechnung}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={t('bestellung.rechnungNeuesFenster')}
              >
                {t('bestellung.rechnung')}
              </a>
            )}
            <Nachbestellen bestellung={bestellung} />
          </div>
        </div>

        <div className="bestellansicht-spalte">
          <section className="bestellkasten">
            <h2 className="bestellkasten-titel">{t('bestellung.zahlung')}</h2>

            <ul className="bestellposten">
              {bestellung.lines.map((zeile, index) => (
                <Posten
                  key={`z-${zeile.name}-${zeile.variant}-${index}`}
                  zeile={zeile}
                  mitPreis
                />
              ))}
            </ul>

            {/* Alle Zahlen kommen aus derselben Antwort. Eine hier
                nachgerechnete Summe wäre eine zweite Wahrheit — und die
                abweichende fiele erst auf der Rechnung auf. */}
            <div className="bestellsummen">
              <p className="summe">
                <span>{t('bestellung.zwischensumme')}</span>
                <span>{preis(bestellung.subtotalGross)}</span>
              </p>
              {/* Der Rabatt mindert den Preis — er steht deshalb ÜBER dem
                  Gesamtbetrag und ist darin schon abgezogen. */}
              {(bestellung.discountGross ?? 0) > 0 && (
                <p className="summe summe-rabatt">
                  <span>
                    {bestellung.discountName || t('rabatt.gewaehrt')}
                    {bestellung.discountCode && (
                      <span className="rabatt-marke rabatt-marke-klein">
                        {bestellung.discountCode}
                      </span>
                    )}
                  </span>
                  <span>−{preis(bestellung.discountGross ?? 0)}</span>
                </p>
              )}
              <p className="summe">
                <span>{t('bestellung.versand')}</span>
                <span>
                  {/* Null ist nicht gleich null: Bei einer Abholung gibt es
                      keinen Versand, bei einer Lieferung über der Freigrenze
                      ist er verschenkt. „Entfällt" für beides zu schreiben
                      unterschlüge dem Kunden, dass er gerade etwas gespart
                      hat. */}
                  {bestellung.shippingGross > 0
                    ? preis(bestellung.shippingGross)
                    : bestellung.fulfilment === 'pickup'
                      ? t('bestellung.versandEntfaellt')
                      : t('bestellung.versandKostenlos')}
                </span>
              </p>
              {/* Die Steuer als eigene Zeile und nicht als Satz darunter: In
                  der Vorlage steht sie zwischen Versand und Gesamt, und wer
                  eine Rechnung prüft, sucht sie genau dort. */}
              <p className="summe">
                <span>{t('bestellung.steuerZeile')}</span>
                <span>{preis(bestellung.totalTax)}</span>
              </p>
              <p className="summe summe-gesamt">
                <span>{t('bestellung.gesamt')}</span>
                <strong>{preis(bestellung.totalGross)}</strong>
              </p>

              {/* Der Wertgutschein steht UNTER dem Gesamtbetrag, und das ist
                  kein Gestaltungseinfall.

                  Er mindert den Preis nicht, er bezahlt ihn: Die Ware kostet,
                  was sie kostet, und die Umsatzsteuer bleibt auf dem vollen
                  Betrag stehen. Stünde er oben bei den Abzügen, sähe es aus,
                  als sei weniger Umsatz gemacht worden — und genau so rechnet
                  man am Jahresende falsch.

                  Darunter, was tatsächlich noch zu zahlen war. */}
              {(bestellung.voucherGross ?? 0) > 0 && (
                <>
                  <p className="summe summe-gutschein">
                    <span>{t('bestellung.gutschein')}</span>
                    <span>−{preis(bestellung.voucherGross ?? 0)}</span>
                  </p>
                  <p className="summe summe-gesamt">
                    <span>{t('bestellung.nochZuZahlen')}</span>
                    <strong>
                      {preis(bestellung.totalGross - (bestellung.voucherGross ?? 0))}
                    </strong>
                  </p>
                </>
              )}
            </div>
          </section>

          <Anschrift bestellung={bestellung} />
        </div>
      </div>
    </section>
  )
}

/**
 * Die Lieferanschrift — oder die Auskunft, dass es keine gibt.
 *
 * Bei einer Abholung fehlt sie mit Absicht: Ein leerer Kasten mit der
 * Überschrift „Lieferanschrift" sieht aus, als hätte jemand vergessen, sie
 * einzutragen. Dort steht deshalb, was stattdessen gilt.
 */
function Anschrift({ bestellung }: { bestellung: Order }) {
  const { t } = useSprache()
  const landName = useLandName()
  const anschrift = bestellung.shippingAddress
  const abholung = bestellung.fulfilment === 'pickup'

  if (abholung || !anschrift) {
    return (
      <section className="bestellkasten">
        <h2 className="bestellkasten-titel">
          {abholung ? t('bestellung.abholung') : t('bestellung.lieferanschrift')}
        </h2>
        <p className="feld-hinweis">
          {abholung ? t('bestellung.abholungText') : t('bestellung.keineAnschrift')}
        </p>
      </section>
    )
  }

  return (
    <section className="bestellkasten">
      <h2 className="bestellkasten-titel">{t('bestellung.lieferanschrift')}</h2>
      {/* Eine Adresse ist kein Fließtext: Sie steht zeilenweise, wie sie auf
          dem Paket steht. Was leer ist, fällt weg — eine Leerzeile mitten in
          der Anschrift sieht aus wie ein Fehler. */}
      <address className="bestellanschrift">
        {[
          anschrift.name,
          anschrift.company,
          anschrift.street,
          anschrift.extra,
          [anschrift.postalCode, anschrift.city].filter(Boolean).join(' '),
          // „Deutschland" und nicht „DE": Der Code ist die Sprache der
          // Schnittstelle, nicht die des Kunden.
          anschrift.country ? landName(anschrift.country) : '',
        ]
          .map((zeile) => zeile?.trim())
          .filter(Boolean)
          .map((zeile, index) => (
            <span key={`${zeile}-${index}`}>{zeile}</span>
          ))}
      </address>
    </section>
  )
}

/**
 * „Noch einmal bestellen".
 *
 * Legt jede Position erneut in den Warenkorb — Zeile für Zeile und nicht auf
 * einen Schlag: Der Server kennt keinen Aufruf, der eine ganze Bestellung
 * wiederholt, und er soll auch keinen bekommen. Die Positionen sind
 * eingefroren; ob es die Variante heute noch gibt, entscheidet er bei jeder
 * einzelnen.
 *
 * Und deshalb steht am Ende eine Auskunft, wie viele übernommen wurden.
 * Stillschweigend die Hälfte in den Korb zu legen wäre der Fehler, der erst an
 * der Kasse auffällt — und dort niemandem mehr erklärbar ist.
 */
function Nachbestellen({ bestellung }: { bestellung: Order }) {
  const { t } = useSprache()
  const { zustellart, land, neuLaden, oeffnen } = useCart()
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  async function nachbestellen() {
    if (laeuft) return
    setLaeuft(true)
    setMeldung(null)

    const gesamt = bestellung.lines.length
    let uebernommen = 0

    for (const zeile of bestellung.lines) {
      // Ohne Variantenkennung gibt es nichts, was in den Korb ginge — etwa,
      // weil der Artikel aus dem Sortiment ist oder ein älterer Server das
      // Feld nicht mitschickt.
      if (!zeile.variantId) continue
      try {
        // Nacheinander und nicht alle zugleich: Der Aufruf setzt eine absolute
        // Menge. Zwei gleichzeitige Schreibvorgänge auf denselben Warenkorb
        // ergäben eine Menge, die keiner der beiden gewollt hat.
        await setCartLine(zeile.variantId, zeile.quantity, zustellart, land)
        uebernommen += 1
      } catch {
        // Ausverkauft oder nicht mehr im Sortiment. Kein Abbruch: Die übrigen
        // Positionen sollen trotzdem hineinkommen.
      }
    }

    neuLaden()
    setLaeuft(false)

    if (uebernommen === 0) {
      setMeldung(t('bestellung.nachbestelltKeine'))
      return
    }
    setMeldung(
      uebernommen === gesamt
        ? t('bestellung.nachbestelltAlle')
        : t('bestellung.nachbestelltTeil', { anzahl: uebernommen, gesamt }),
    )
    // Der Warenkorb geht auf, sobald etwas drin ist: „Noch einmal bestellen"
    // ist ein Versprechen, und der offene Korb ist sein Beleg.
    oeffnen()
  }

  return (
    <>
      <button className="knopf knopf-schlicht" type="button" disabled={laeuft} onClick={() => void nachbestellen()}>
        {laeuft ? t('bestellung.legtAn') : t('bestellung.nochEinmal')}
      </button>
      {meldung && (
        <p className="hinweis bestellansicht-meldung" role="status">
          {meldung}
        </p>
      )}
    </>
  )
}

/**
 * Eine Position mit Bild.
 *
 * Das Bild ist nicht Schmuck: Eine Bestellung wird Wochen später aufgerufen,
 * und „Roter Pullover (M · blau)" ruft niemandem ins Gedächtnis, was er da
 * gekauft hat — das Bild tut es sofort.
 *
 * Fehlt es (ein älterer Server schickt keines), steht dort keine leere Fläche,
 * sondern eine getönte Kachel mit dem Anfangsbuchstaben. Eine Reihe leerer
 * Rahmen sieht aus wie ein Ladefehler; eine Reihe getönter Kacheln sieht aus
 * wie Absicht — und das ist sie auch.
 */
function Posten({ zeile, mitPreis = false }: { zeile: OrderLine; mitPreis?: boolean }) {
  const { t } = useSprache()
  const preis = usePriceFormat()

  return (
    <li className="bestellposten-zeile">
      {zeile.imageUrl ? (
        <img
          className="bestellposten-bild"
          src={zeile.imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="bestellposten-bild bestellposten-ersatz" aria-hidden="true">
          {zeile.name.trim().charAt(0).toUpperCase() || '·'}
        </span>
      )}

      <span className="bestellposten-text">
        <span className="bestellposten-name">{zeile.name}</span>
        {/* Name und Achsenwerte kommen aus der Kasse und bleiben, wie sie
            sind — siehe i18n.tsx. */}
        {zeile.variant && <span className="feld-hinweis">{zeile.variant}</span>}
        {mitPreis && (
          <span className="feld-hinweis">
            {t('bestellung.stueck', { anzahl: zeile.quantity })}{' '}
            {t('bestellung.jeStueck', { preis: preis(zeile.unitGross) })}
          </span>
        )}
      </span>

      <span className="bestellposten-preis">
        {mitPreis ? (
          <>
            {/* Der alte Preis durchgestrichen daneben, wenn ein Rabatt auf
                diese Zeile entfiel. Nur das Ergebnis zu zeigen hiesse, einen
                Preis zu nennen, den der Kunde nirgends wiederfindet: Auf der
                Produktseite stand der volle. */}
            {(zeile.discountGross ?? 0) > 0 && (
              <span className="preis-vorher">
                {preis(zeile.totalGross + (zeile.discountGross ?? 0))}
              </span>
            )}
            {preis(zeile.totalGross)}
          </>
        ) : (
          t('bestellung.stueck', { anzahl: zeile.quantity })
        )}
      </span>
    </li>
  )
}
